import { partition, sortBy } from 'lodash';

import { isDefined, type Point } from '#shared';

type WithCoords = { id: string; donorHospital?: { lonLat?: Point } };
type CoordsMap = Record<string, Point>;

const getCoords = (m: CoordsMap, it: WithCoords): Point | undefined =>
  m[it.id] ?? it.donorHospital?.lonLat;

const xOf = (m: CoordsMap, it: WithCoords, centerX: number) =>
  getCoords(m, it)?.[0] ?? centerX;

const yOf = (m: CoordsMap, it: WithCoords) => getCoords(m, it)?.[1] ?? 0;

export const distributeAndSortItemsByPosition = <T extends WithCoords>(
  items: readonly T[],
  coordinatesMap: CoordsMap | null,
  maxItemsPerSide: number | undefined,
  geoMapCenter: Point,
  minItemsPerSide = 1, // Default minimum of 1 item per side when possible
): { leftItems: T[]; rightItems: T[] } => {
  // Handle empty items case
  if (items.length === 0) {
    return { leftItems: [], rightItems: [] };
  }

  // no coords yet → even split; preserve all items
  if (!coordinatesMap) {
    const mid = Math.ceil(items.length / 2);
    return { leftItems: items.slice(0, mid), rightItems: items.slice(mid) };
  }

  const [centerX] = geoMapCenter;

  // assign by X using partition (left: <= centerX, right: > centerX)
  const [leftRaw, rightRaw] = partition(items, it => {
    const c = getCoords(coordinatesMap, it);
    return !c || c[0] <= centerX;
  });

  // 2) sort columns by Y (north → south)
  let left = sortBy(leftRaw, it => yOf(coordinatesMap, it));
  let right = sortBy(rightRaw, it => yOf(coordinatesMap, it));

  // 3a) Balance for minimum items per side when possible
  if (minItemsPerSide > 0 && items.length >= 2) {
    if (left.length === 0 && right.length >= 2) {
      // Move closest items from right to left
      const moveCount = Math.min(
        minItemsPerSide,
        right.length - minItemsPerSide,
      );
      if (moveCount > 0) {
        const candidates = sortBy(right, it =>
          Math.abs(xOf(coordinatesMap, it, centerX) - centerX),
        ).slice(0, moveCount);

        const ids = new Set(candidates.map(c => c.id));
        right = right.filter(i => !ids.has(i.id));
        left = candidates;
      }
    } else if (right.length === 0 && left.length >= 2) {
      // Move closest items from left to right
      const moveCount = Math.min(
        minItemsPerSide,
        left.length - minItemsPerSide,
      );
      if (moveCount > 0) {
        const candidates = sortBy(left, it =>
          Math.abs(xOf(coordinatesMap, it, centerX) - centerX),
        ).slice(0, moveCount);

        const ids = new Set(candidates.map(c => c.id));
        left = left.filter(i => !ids.has(i.id));
        right = candidates;
      }
    }
  }

  // 3b) Handle max items per side cap
  if (isDefined(maxItemsPerSide)) {
    const cap = maxItemsPerSide;

    const leftOverflow = Math.max(0, left.length - cap);
    const rightOverflow = Math.max(0, right.length - cap);
    const leftCapacity = Math.max(0, cap - left.length);
    const rightCapacity = Math.max(0, cap - right.length);

    if (rightOverflow > 0 && leftCapacity > 0) {
      const moveCount = Math.min(rightOverflow, leftCapacity);
      const candidates = sortBy(right, it =>
        Math.abs(xOf(coordinatesMap, it, centerX) - centerX),
      ).slice(0, moveCount);

      const ids = new Set(candidates.map(c => c.id));
      right = right.filter(i => !ids.has(i.id));
      left = left.concat(candidates);
    } else if (leftOverflow > 0 && rightCapacity > 0) {
      const moveCount = Math.min(leftOverflow, rightCapacity);
      const candidates = sortBy(left, it =>
        Math.abs(xOf(coordinatesMap, it, centerX) - centerX),
      ).slice(0, moveCount);

      const ids = new Set(candidates.map(c => c.id));
      left = left.filter(i => !ids.has(i.id));
      right = right.concat(candidates);
    }
  }

  // keep both columns vertically tidy
  left = sortBy(left, it => yOf(coordinatesMap, it));
  right = sortBy(right, it => yOf(coordinatesMap, it));
  // totals preserved
  return { leftItems: left, rightItems: right };
};
